import { useState, useEffect } from 'react';
import { Button, Input, Label, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { Accordion, AccordionItem } from '@/components/ui/Accordion';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AutoTransferRule, AutoTransferConditions } from '../../types/auto-transfer.types';
import { UserMultiSelect } from './UserMultiSelect';
import { VehicleMultiSelect } from './VehicleMultiSelect';
import { StateMultiSelect } from './StateMultiSelect';
import { CityAutocomplete } from './CityAutocomplete';
import { NeighborhoodAutocomplete } from './NeighborhoodAutocomplete';
import { MultiSelect, MultiSelectOption } from './MultiSelect';
import { RangeInput } from './RangeInput';
import { DayOfWeekSelector } from './DayOfWeekSelector';
import { TimeInput } from './TimeInput';
import { apiService } from '@/services/api';
import { useAutoTransferOptions } from '../hooks/useAutoTransferOptions';

interface RuleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    conditions: Record<string, unknown>;
    assignedUserIds: string[];
  }) => Promise<void>;
  rule: AutoTransferRule | null;
  loading: boolean;
}

interface Brand {
  id: string;
  name: string;
}

interface Model {
  id: string;
  name: string;
  brandId: string;
}

interface LeadSource {
  id: string;
  name: string;
  key: string;
}

export function RuleFormModal({ open, onClose, onSave, rule, loading }: RuleFormModalProps) {
  const { t } = useTranslation();
  const { options: vehicleOptions, loading: optionsLoading } = useAutoTransferOptions();

  // Basic fields
  const [name, setName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Rule type
  const [ruleType, setRuleType] = useState<'specific_vehicles' | 'characteristics'>('characteristics');
  const [vehicleIds, setVehicleIds] = useState<string[]>([]);
  const [branchIds, setBranchIds] = useState<string[]>([]);

  // Geographic filters
  const [regions, setRegions] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<string[]>([]);

  // Vehicle filters
  const [categories, setCategories] = useState<string[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<string[]>([]);
  const [vehicleConditions, setVehicleConditions] = useState<string[]>([]);
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [modelIds, setModelIds] = useState<string[]>([]);
  const [versionIds, setVersionIds] = useState<string[]>([]);
  const [fuelTypes, setFuelTypes] = useState<string[]>([]);
  const [transmissions, setTransmissions] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [armorLevels, setArmorLevels] = useState<string[]>([]);

  // Range filters
  const [minYear, setMinYear] = useState<number | null>(null);
  const [maxYear, setMaxYear] = useState<number | null>(null);
  const [minPrice, setMinPrice] = useState<number | null>(null);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [minMileage, setMinMileage] = useState<number | null>(null);
  const [maxMileage, setMaxMileage] = useState<number | null>(null);

  // Schedule
  const [scheduleDays, setScheduleDays] = useState<string[]>([]);
  const [scheduleStart, setScheduleStart] = useState<string | null>(null);
  const [scheduleEnd, setScheduleEnd] = useState<string | null>(null);

  // Lead sources
  const [sources, setSources] = useState<string[]>([]);
  const [leadSourceKeys, setLeadSourceKeys] = useState<string[]>([]);

  // Data from API
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [branches, setBranches] = useState<Array<{ id: string; name: string }>>([]);

  const [saving, setSaving] = useState(false);

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setDataLoading(true);

        // Load active lead sources from /leads/media
        const leadMediaData = await apiService.get<Array<{
          id: string;
          key: string;
          label: string;
          isOfficial: boolean;
          isActive: boolean;
          sortOrder: number;
        }>>('/leads/media');

        const activeSources = Array.isArray(leadMediaData)
          ? leadMediaData
              .filter(media => media.isActive)
              .map(media => ({
                id: media.id,
                name: media.label,
                key: media.key,
              }))
          : [];
        setLeadSources(activeSources);

        // Load branches from /dealership/branches
        const branchesData = await apiService.get<Array<{
          id: string;
          name: string;
        }>>('/dealership/branches');
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (error) {
        console.error('Failed to load data:', error);
        setLeadSources([]);
        setBranches([]);
      } finally {
        setDataLoading(false);
      }
    };

    loadData();
  }, []);

  // Load brands when categories change
  useEffect(() => {
    if (categories.length === 0) {
      setBrands([]);
      return;
    }

    const loadBrands = async () => {
      try {
        // Load brands filtered by categories (multiple)
        const categoriesParam = categories.map(cat => `categories=${cat}`).join('&');
        const brandsData = await apiService.get<Brand[]>(
          `/catalog/brands?${categoriesParam}`
        );
        setBrands(Array.isArray(brandsData) ? brandsData : []);
      } catch (error) {
        console.error('Failed to load brands:', error);
        setBrands([]);
      }
    };

    loadBrands();
  }, [categories]);

  // Load models when brands change
  useEffect(() => {
    if (brandIds.length === 0) {
      setModels([]);
      return;
    }

    const loadModels = async () => {
      try {
        // Load models for each selected brand
        const modelsPromises = brandIds.map(brandId =>
          apiService.getBrandModels(brandId)
        );
        const modelsArrays = await Promise.all(modelsPromises);
        const allModels = modelsArrays.flat();
        setModels(Array.isArray(allModels) ? allModels : []);
      } catch (error) {
        console.error('Failed to load models:', error);
        setModels([]);
      }
    };

    loadModels();
  }, [brandIds]);

  // Initialize form from rule
  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setSelectedUsers(rule.assignedUsers?.map(u => u.userId) || []);

      const cond = rule.conditions || {};
      setRuleType(cond.ruleType || 'characteristics');
      setVehicleIds(cond.vehicleIds || []);
      setBranchIds(cond.branchIds || []);
      setRegions(cond.regions || []);
      setStates(cond.states || []);
      setCities(cond.cities || []);
      setNeighborhoods(cond.neighborhoods || []);
      setCategories(cond.categories || []);
      setVehicleTypes(cond.vehicleTypes || []);
      setVehicleConditions(cond.conditions || []);
      setBrandIds(cond.brandIds || []);
      setModelIds(cond.modelIds || []);
      setVersionIds(cond.versionIds || []);
      setFuelTypes(cond.fuelTypes || []);
      setTransmissions(cond.transmissions || []);
      setColors(cond.colors || []);
      setArmorLevels(cond.armorLevels || []);
      setMinYear(cond.minYear ?? null);
      setMaxYear(cond.maxYear ?? null);
      setMinPrice(cond.minPrice ?? null);
      setMaxPrice(cond.maxPrice ?? null);
      setMinMileage(cond.minMileage ?? null);
      setMaxMileage(cond.maxMileage ?? null);
      setScheduleDays(cond.scheduleDays || []);
      setScheduleStart(cond.scheduleStart ?? null);
      setScheduleEnd(cond.scheduleEnd ?? null);
      setSources(cond.sources || []);
      setLeadSourceKeys(cond.leadSourceKeys || []);
    } else {
      // Reset form
      setName('');
      setSelectedUsers([]);
      setRuleType('characteristics');
      setVehicleIds([]);
      setBranchIds([]);
      setRegions([]);
      setStates([]);
      setCities([]);
      setNeighborhoods([]);
      setCategories([]);
      setVehicleTypes([]);
      setVehicleConditions([]);
      setBrandIds([]);
      setModelIds([]);
      setVersionIds([]);
      setFuelTypes([]);
      setTransmissions([]);
      setColors([]);
      setArmorLevels([]);
      setMinYear(null);
      setMaxYear(null);
      setMinPrice(null);
      setMaxPrice(null);
      setMinMileage(null);
      setMaxMileage(null);
      setScheduleDays([]);
      setScheduleStart(null);
      setScheduleEnd(null);
      setSources([]);
      setLeadSourceKeys([]);
    }
  }, [rule, open]);

  const handleSave = async () => {
    if (!name.trim()) return;

    const conditions: AutoTransferConditions = {};

    // Only include vehicle IDs for specific vehicles rule type (ruleType is UI-only, not sent to backend)
    if (ruleType === 'specific_vehicles' && vehicleIds.length > 0) {
      conditions.vehicleIds = vehicleIds;
    }

    // Include branchIds if any branches are selected
    if (branchIds.length > 0) {
      conditions.branchIds = branchIds;
    }

    // NOTE: ruleType is UI-only field, not sent to backend
    // Backend doesn't accept this field in conditions DTO

    // Only include non-empty conditions
    if (regions.length > 0) conditions.regions = regions;
    if (states.length > 0) conditions.states = states;
    if (cities.length > 0) conditions.cities = cities;
    if (neighborhoods.length > 0) conditions.neighborhoods = neighborhoods;
    if (categories.length > 0) conditions.categories = categories;
    if (vehicleTypes.length > 0) conditions.vehicleTypes = vehicleTypes;
    if (vehicleConditions.length > 0) conditions.conditions = vehicleConditions;
    if (brandIds.length > 0) conditions.brandIds = brandIds;
    if (modelIds.length > 0) conditions.modelIds = modelIds;
    if (versionIds.length > 0) conditions.versionIds = versionIds;
    if (fuelTypes.length > 0) conditions.fuelTypes = fuelTypes;
    if (transmissions.length > 0) conditions.transmissions = transmissions;
    if (colors.length > 0) conditions.colors = colors;
    if (armorLevels.length > 0) conditions.armorLevels = armorLevels;
    if (minYear !== null) conditions.minYear = minYear;
    if (maxYear !== null) conditions.maxYear = maxYear;
    if (minPrice !== null) conditions.minPrice = minPrice;
    if (maxPrice !== null) conditions.maxPrice = maxPrice;
    if (minMileage !== null) conditions.minMileage = minMileage;
    if (maxMileage !== null) conditions.maxMileage = maxMileage;
    if (scheduleDays.length > 0) conditions.scheduleDays = scheduleDays;
    if (scheduleStart) conditions.scheduleStart = scheduleStart;
    if (scheduleEnd) conditions.scheduleEnd = scheduleEnd;
    if (sources.length > 0) conditions.sources = sources;
    if (leadSourceKeys.length > 0) conditions.leadSourceKeys = leadSourceKeys;

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        conditions: conditions as Record<string, unknown>,
        assignedUserIds: selectedUsers,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const canSaveRule = name.trim().length > 0 && selectedUsers.length > 0;

  if (!open) return null;

  // Options for selects
  const regionOptions: MultiSelectOption[] = [
    { value: 'NORTH', label: t('leads.autoTransfer.regionNorth') },
    { value: 'NORTHEAST', label: t('leads.autoTransfer.regionNortheast') },
    { value: 'MIDWEST', label: t('leads.autoTransfer.regionMidwest') },
    { value: 'SOUTHEAST', label: t('leads.autoTransfer.regionSoutheast') },
    { value: 'SOUTH', label: t('leads.autoTransfer.regionSouth') },
  ];

  const categoryOptions: MultiSelectOption[] = vehicleOptions.categories;
  const vehicleTypeOptions: MultiSelectOption[] = vehicleOptions.vehicleTypes;
  const conditionOptions: MultiSelectOption[] = vehicleOptions.conditions;
  const fuelTypeOptions: MultiSelectOption[] = vehicleOptions.fuelTypes;
  const transmissionOptions: MultiSelectOption[] = vehicleOptions.transmissions;
  const colorOptions: MultiSelectOption[] = vehicleOptions.colors;
  const doorOptions: MultiSelectOption[] = vehicleOptions.doors;
  const armorLevelOptions: MultiSelectOption[] = vehicleOptions.armorLevels;
  const characteristicOptions: MultiSelectOption[] = vehicleOptions.characteristics;

  const brandOptions: MultiSelectOption[] = Array.isArray(brands)
    ? brands.map(b => ({
        value: b.id,
        label: b.name,
      }))
    : [];

  const modelOptions: MultiSelectOption[] = Array.isArray(models)
    ? models.map(m => ({
        value: m.id,
        label: m.name,
      }))
    : [];

  const leadSourceOptions: MultiSelectOption[] = Array.isArray(leadSources)
    ? leadSources.map(ls => ({
        value: ls.id,
        label: ls.name,
      }))
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold text-foreground">
          {rule
            ? t('leads.autoTransfer.editRule')
            : t('leads.autoTransfer.newRule')}
        </h1>
      </div>

      <Card className="max-w-4xl">
        <CardHeader>
          <CardTitle>{rule ? t('leads.autoTransfer.editRule') : t('leads.autoTransfer.newRule')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('leads.autoTransfer.ruleName')}</Label>
              <Input
                id="name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('leads.autoTransfer.ruleNamePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('leads.autoTransfer.assignedUsers')}</Label>
              <UserMultiSelect
                value={selectedUsers}
                onChange={setSelectedUsers}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('leads.autoTransfer.branches')}</Label>
              <MultiSelect
                options={branches.map(b => ({ value: b.id, label: b.name }))}
                value={branchIds}
                onChange={setBranchIds}
                placeholder={t('leads.autoTransfer.selectBranches')}
              />
              <p className="text-xs text-muted-foreground">
                {t('leads.autoTransfer.branchesDesc')}
              </p>
            </div>
          </div>

          {/* Rule Type Selection */}
          <div className="space-y-3 border-t pt-4">
            <Label>{t('leads.autoTransfer.ruleType')}</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="ruleType"
                  value="characteristics"
                  checked={ruleType === 'characteristics'}
                  onChange={(e) => setRuleType(e.target.value as 'characteristics')}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">
                  {t('leads.autoTransfer.byCharacteristics')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="ruleType"
                  value="specific_vehicles"
                  checked={ruleType === 'specific_vehicles'}
                  onChange={(e) => setRuleType(e.target.value as 'specific_vehicles')}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">
                  {t('leads.autoTransfer.bySpecificVehicles')}
                </span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              {ruleType === 'characteristics'
                ? t('leads.autoTransfer.byCharacteristicsDesc')
                : t('leads.autoTransfer.bySpecificVehiclesDesc')}
            </p>
          </div>

          {/* Specific Vehicles Selection (only shown when ruleType is specific_vehicles) */}
          {ruleType === 'specific_vehicles' && (
            <div className="space-y-2">
              <Label>{t('leads.autoTransfer.selectVehicles')}</Label>
              <VehicleMultiSelect
                value={vehicleIds}
                onChange={setVehicleIds}
              />
            </div>
          )}

          {/* Conditions Sections (only shown when ruleType is characteristics) */}
          {ruleType === 'characteristics' && (
          <Accordion>
            {/* Geographic Filters */}
            <AccordionItem title={
              <div>
                <div className="font-medium">{t('leads.autoTransfer.geographicFilters')}</div>
                <div className="text-xs text-muted-foreground">{t('leads.autoTransfer.geographicFiltersDesc')}</div>
              </div>
            }>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.regions')}</Label>
                  <MultiSelect
                    options={regionOptions}
                    value={regions}
                    onChange={setRegions}
                    placeholder={t('leads.autoTransfer.regionsPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.states')}</Label>
                  <StateMultiSelect
                    value={states}
                    onChange={setStates}
                    selectedRegions={regions}
                  />
                </div>

                <CityAutocomplete
                  value={cities}
                  onChange={setCities}
                  stateIds={states}
                />

                <NeighborhoodAutocomplete
                  value={neighborhoods}
                  onChange={setNeighborhoods}
                />
              </div>
            </AccordionItem>

            {/* Vehicle Filters */}
            <AccordionItem title={
              <div>
                <div className="font-medium">{t('leads.autoTransfer.vehicleFilters')}</div>
                <div className="text-xs text-muted-foreground">{t('leads.autoTransfer.vehicleFiltersDesc')}</div>
              </div>
            }>
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('leads.autoTransfer.categories')}</Label>
                    <MultiSelect
                      options={categoryOptions}
                      value={categories}
                      onChange={setCategories}
                      placeholder={t('leads.autoTransfer.categoriesPlaceholder')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('leads.autoTransfer.vehicleTypes')}</Label>
                    <MultiSelect
                      options={vehicleTypeOptions}
                      value={vehicleTypes}
                      onChange={setVehicleTypes}
                      placeholder={t('leads.autoTransfer.vehicleTypesPlaceholder')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.vehicleConditions')}</Label>
                  <MultiSelect
                    options={conditionOptions}
                    value={vehicleConditions}
                    onChange={setVehicleConditions}
                    placeholder={t('leads.autoTransfer.vehicleConditionsPlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('leads.autoTransfer.brands')}</Label>
                    <MultiSelect
                      options={brandOptions}
                      value={brandIds}
                      onChange={setBrandIds}
                      placeholder={
                        categories.length === 0
                          ? t('leads.autoTransfer.selectCategoriesFirst')
                          : t('leads.autoTransfer.brandsPlaceholder')
                      }
                      disabled={categories.length === 0}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('leads.autoTransfer.models')}</Label>
                    <MultiSelect
                      options={modelOptions}
                      value={modelIds}
                      onChange={setModelIds}
                      placeholder={
                        brandIds.length === 0
                          ? t('leads.autoTransfer.selectBrandsFirst')
                          : t('leads.autoTransfer.modelsPlaceholder')
                      }
                      disabled={brandIds.length === 0}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('leads.autoTransfer.fuelTypes')}</Label>
                    <MultiSelect
                      options={fuelTypeOptions}
                      value={fuelTypes}
                      onChange={setFuelTypes}
                      placeholder={t('leads.autoTransfer.fuelTypesPlaceholder')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('leads.autoTransfer.transmissions')}</Label>
                    <MultiSelect
                      options={transmissionOptions}
                      value={transmissions}
                      onChange={setTransmissions}
                      placeholder={t('leads.autoTransfer.transmissionsPlaceholder')}
                    />
                  </div>
                </div>
              </div>
            </AccordionItem>

            {/* Price and Range */}
            <AccordionItem title={
              <div>
                <div className="font-medium">{t('leads.autoTransfer.priceAndRange')}</div>
                <div className="text-xs text-muted-foreground">{t('leads.autoTransfer.priceAndRangeDesc')}</div>
              </div>
            }>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.yearRange')}</Label>
                  <RangeInput
                    minValue={minYear}
                    maxValue={maxYear}
                    onMinChange={setMinYear}
                    onMaxChange={setMaxYear}
                    minLabel={t('leads.autoTransfer.minYear')}
                    maxLabel={t('leads.autoTransfer.maxYear')}
                    minPlaceholder="2010"
                    maxPlaceholder="2024"
                    step={1}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.priceRange')}</Label>
                  <RangeInput
                    minValue={minPrice}
                    maxValue={maxPrice}
                    onMinChange={setMinPrice}
                    onMaxChange={setMaxPrice}
                    minLabel={t('leads.autoTransfer.minPrice')}
                    maxLabel={t('leads.autoTransfer.maxPrice')}
                    minPlaceholder="50000"
                    maxPlaceholder="150000"
                    step={1000}
                    prefix="R$"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.mileageRange')}</Label>
                  <RangeInput
                    minValue={minMileage}
                    maxValue={maxMileage}
                    onMinChange={setMinMileage}
                    onMaxChange={setMaxMileage}
                    minLabel={t('leads.autoTransfer.minMileage')}
                    maxLabel={t('leads.autoTransfer.maxMileage')}
                    minPlaceholder="0"
                    maxPlaceholder="100000"
                    step={1000}
                  />
                </div>
              </div>
            </AccordionItem>

            {/* Schedule */}
            <AccordionItem title={
              <div>
                <div className="font-medium">{t('leads.autoTransfer.schedule')}</div>
                <div className="text-xs text-muted-foreground">{t('leads.autoTransfer.scheduleDesc')}</div>
              </div>
            }>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.scheduleDays')}</Label>
                  <DayOfWeekSelector
                    value={scheduleDays}
                    onChange={setScheduleDays}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <TimeInput
                    value={scheduleStart}
                    onChange={setScheduleStart}
                    label={t('leads.autoTransfer.scheduleStart')}
                    placeholder="09:00"
                  />

                  <TimeInput
                    value={scheduleEnd}
                    onChange={setScheduleEnd}
                    label={t('leads.autoTransfer.scheduleEnd')}
                    placeholder="18:00"
                  />
                </div>
              </div>
            </AccordionItem>

            {/* Lead Sources */}
            <AccordionItem title={
              <div>
                <div className="font-medium">{t('leads.autoTransfer.leadSources')}</div>
                <div className="text-xs text-muted-foreground">{t('leads.autoTransfer.leadSourcesDesc')}</div>
              </div>
            }>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.sources')}</Label>
                  <MultiSelect
                    options={leadSourceOptions}
                    value={sources}
                    onChange={setSources}
                    placeholder={dataLoading ? t('common.loading') : t('leads.autoTransfer.sourcesPlaceholder')}
                    disabled={dataLoading}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('leads.autoTransfer.leadSourceKeys')}</Label>
                  <Input
                    value={leadSourceKeys.join(', ')}
                    onChange={e => setLeadSourceKeys(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder={t('leads.autoTransfer.leadSourceKeysPlaceholder')}
                  />
                </div>
              </div>
            </AccordionItem>
          </Accordion>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleCancel} disabled={saving || loading}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving || loading || !canSaveRule}>
              {(saving || loading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
